import { Data, Result } from 'effect';

/**
 * Which face of the window a drawing shows.
 *
 * The engine draws both into every drawing, as two `<g class="outside">` and two `<g class="inside">` — the window itself and its dimension marks, once per side — and hides one pair. Turning the drawing around is therefore a matter of which pair is shown rather than of fetching anything.
 *
 * These are the engine's own words, and the same ones `svgviewer.css` and `color.css` are written in. The buttons say "Udvendig" and "Indvendig", which is the same pair in Danish.
 */
export type DrawingSide = 'inside' | 'outside';

/**
 * The class that turns a drawing around, set on the `<svg>` itself.
 *
 * One class rather than a `display` written onto each of the four groups. The groups carry no `display` of their own — the single rule `svg .inside { display: none }` in `svgviewer.css` is what hides a pair — so outside is what a drawing shows unless something says otherwise, and this is that something. Exported
 * because the rule that reads it and the code that sets it must not drift apart.
 *
 * A root class is how this system already works: `color.css` selects through `svg.simplemode`.
 */
export const SHOW_INSIDE_CLASS = 'show-inside';

/**
 * The font size the drawing's own text is set in, in the units its viewBox is drawn in.
 *
 * Pinned rather than inherited, unlike the font family. Inside a viewBox a length is a user unit, so an inherited size would make the labels grow and shrink with whatever typography the host app happens to use — the drawing's proportions would depend on the page it sits on. Fourteen is what the existing viewer
 * renders, whose page sets `html { font-size: 14px }`.
 */
const DRAWING_FONT_SIZE = 14;

/**
 * What the drawing needs that `svgviewer.css` cannot say inside a shadow root.
 *
 * That file is the whole page of the viewer it was written for, and thirteen of its rules are anchored on `body`, `html` or `:root`. None of those match inside a shadow tree. Most of the thirteen we are glad to lose — they size the drawing to the browser window, `calc(79vw)` by `calc(69vh)`, which is not how a drawing
 * sits in a section. These are the ones we are not:
 *
 * - The custom property. Five rules in that file read it with `var()`, and a custom property is declared where it can be inherited from — `:host` is that place here, `:root` is not.
 * - The two hover rules, which are the drawing's only interaction feedback. Same declarations, without the `body:not(.thumbnail)` prefix that kept them from applying.
 * - `overflow: visible`, which the drawing needs: its measurement labels are drawn outside the viewBox, and a viewport that clipped them would cut off the dimensions.
 *
 * The two rules for {@link SHOW_INSIDE_CLASS} are additions rather than replacements: they invert `svg .inside { display: none }` for one drawing at a time. Specificity carries them — a class on the root plus a class on the group beats that rule whichever order the sheets are adopted in — so they do not depend on this
 * sheet coming after the engine's.
 *
 * `.alt-measurement` is hidden for a plainer reason: each side's measurement group holds both a `standard-measurement` and an `alt-measurement`, the engine hides neither, and the two are drawn one on top of the other. Standard is the one to show, so the alternative is turned off. Nothing in `svgviewer.css` has an
 * opinion about either, so one rule settles it — and since `display` is not inherited, this is independent of which side is showing.
 *
 * Taken out of the flow with `position: absolute`, which is the one rule here that is not a replacement for anything and is load-bearing all the same. A drawing declares a viewBox and no width or height, so in the flow it sizes itself from its own aspect ratio — 500 by 1000 means twice as tall as the section is wide —
 * and `height: 100%` cannot stop it, because the host it would resolve against is a flex item whose own height is auto. The section then grows to that height and squeezes every sibling to nothing. Positioned, the drawing contributes no height at all and fills whatever the layout gives the viewer. `Component` sets
 * `position: relative` on every element it styles, so the host is already the containing block. It is what `libs/core`'s own `SVG` does with the element it builds, and for this reason.
 *
 * The rest is ours rather than a replacement: the element is sized to its host, and the font is left to be inherited so a drawing is set in the same face as the app around it. Only the size is pinned — see {@link DRAWING_FONT_SIZE}.
 *
 * Nothing here sets the host's `display` or its size. `Component` writes both as inline styles on the element itself, and an inline style beats `:host` — so a rule for either would be a rule that silently does nothing.
 *
 * Everything in `svgviewer.css` that matches nothing in here is left alone rather than pruned. A rule for a `ul`, an `input` or a `#header` cannot reach out of a shadow root and finds nothing inside one, so it costs nothing — and editing a file we do not own, to remove what is already inert, would only be a way to
 * break when someone else changes it.
 */
const supplementCss = `
	:host {
		--clearance-overlay-stroke-width: 5px;
		font-size: ${DRAWING_FONT_SIZE}px;
	}

	svg {
		height: 100%;
		left: 0;
		overflow: visible;
		position: absolute;
		top: 0;
		width: 100%;
	}

	svg .alt-measurement {
		display: none;
	}

	svg.${SHOW_INSIDE_CLASS} .inside {
		display: inline;
	}

	svg.${SHOW_INSIDE_CLASS} .outside {
		display: none;
	}

	svg .operation:hover {
		fill: #808080;
	}

	path.glasslist[data-coating]:hover,
	path.effekt-frame[data-coating]:hover,
	path.frame[data-coating]:hover,
	path.effekt-sash[data-coating]:hover,
	path.sash[data-coating]:hover {
		fill: #808080;
	}
`;

/**
 * The supplement as one sheet, built once and shared by every viewer.
 *
 * A constructed stylesheet is made to be adopted by many shadow roots at once, so there is one of these for the page rather than one per component. It is built here, when the module loads, because the text is a constant: there is nothing to wait for and nothing that can fail.
 */
export const drawingStyleSupplement: CSSStyleSheet = new CSSStyleSheet();
drawingStyleSupplement.replaceSync(supplementCss);

/**
 * The element a browser reports a parse failure in, since an XML parser cannot throw.
 *
 * Selected by local name rather than by namespace, because where it lands is nobody's standard and the engines disagree: Chromium answers a failed parse with an XHTML `<html>` document holding the `parsererror`, Firefox with a document rooted in one in a Mozilla namespace of its own. A type selector matches in any
 * namespace, so both are caught by the same line — and the element is searched for anywhere in the document, since Chromium keeps whatever it managed to read beside it.
 */
const PARSER_ERROR_SELECTOR = 'parsererror';

/**
 * The media type a drawing is parsed as.
 *
 * XML rather than HTML, because the engine writes an XML declaration and because HTML parsing repairs what it cannot read instead of saying so. A drawing we cannot parse has to be reportable.
 */
const SVG_MEDIA_TYPE = 'image/svg+xml';

/** How much of the markup a failure quotes, so an error stays readable next to a 40 kB drawing. */
const QUOTED_LENGTH = 200;

/**
 * One parser for the module, because it holds nothing between calls.
 *
 * `DOMParser` is stateless — every call answers with a fresh document — so a new one per drawing would be an allocation that buys nothing.
 */
const parser = new DOMParser();

/**
 * Raised when markup that was supposed to be a drawing could not be made into one.
 *
 * Carries what the browser said rather than the markup itself: a drawing runs to tens of kilobytes, and an error that quotes all of it is an error nobody reads. `quoted` is the opening of what arrived, which is enough to tell a truncated drawing from an HTML error page the CDN served with a 200.
 */
export class DrawingParseError extends Data.TaggedError('DrawingParseError')<{
	readonly reason: string;
	readonly quoted: string;
}> {}

function failure(reason: string, markup: string): Result.Result<never, DrawingParseError> {
	return Result.fail(new DrawingParseError({ quoted: markup.slice(0, QUOTED_LENGTH), reason }));
}

/**
 * Turns the markup the CDN served into the element it describes.
 *
 * A `Result` rather than an Effect, because there is nothing here to run: it is one synchronous parse with a typed failure, and its caller is a web component that already has the markup in hand. `Effect.fromResult` is the one line that changes that, the day parsing moves into the service.
 *
 * The parse itself cannot fail loudly — `DOMParser` answers with a document whatever it is handed — so everything that could be wrong is checked here instead:
 *
 * - Nothing arrived. An empty body parses to a document with no drawing in it, and would otherwise pass as far as the second check before failing for the wrong reason.
 * - The parser gave up. It says so with a `parsererror` element rather than by failing, and this has to be asked before the check below: a failed parse leaves an `<html>` root behind, so without it every unreadable drawing would be reported as the wrong shape rather than as unreadable. See {@link PARSER_ERROR_SELECTOR}.
 * - The root is not an `<svg>`. What comes back parses cleanly and is not a drawing: an HTML error page, or JSON. `instanceof` is the check rather than a nodeName comparison, because it holds across documents and narrows the type in the same breath.
 *
 * The element is returned as it is, still belonging to the document the parser made. Adopting it is the caller's to do — inserting it does that anyway — and which document it should join is something only the caller knows.
 */
/**
 * Widens the drawing's viewBox to everything it actually draws.
 *
 * The engine declares a viewBox around the window itself — `0 -1000 500 1000` for a window a metre across — and then draws the dimension arrows outside it, two hundred units to the left and above. Everything outside the viewBox is outside the viewport, so it is the measurements, which are the reason anyone reads a
 * technical drawing, that get clipped. Measured on one real drawing: the declared box is 500 by 1000, and what it draws runs from -220,-1220 to 620,120.
 *
 * `getBBox` is the union of what the children draw, in the drawing's own units and independent of the viewport — which is what makes it the right question to ask. Three things about it decided how this is written, and each was measured rather than assumed:
 *
 * - It answers `0 0 0 0` for an element that is not in a rendered document. So this cannot be part of parsing, where the drawing still belongs to the parser's own document: it has to be called once the drawing is in the shadow root.
 * - It does not answer quite the same twice. Setting the box changes the scale the drawing is drawn at, and a text bounding box measured at one scale and converted back to user units is not the one measured at another: on a real drawing the width went 865, then 864.60, then 864.58 over three fits. Converging rather than
 *   running away, but not stable — which is why the box is rounded outward before it is set. Whole units are the same whole units on the next measurement, so a second fit is a no-op, and rounding outward can only ever give the drawing more room than it asked for rather than clip it.
 * - Elements the engine hid with `display: none` are left out, which is right while they are hidden and means this has to be called again by whatever turns them on.
 *
 * Nothing is added around the box. The section pads the viewer, so room to breathe is given in layout rather than in the drawing's own coordinates — where it would have to be a guess at how many units look like a margin.
 *
 * A box with no size is left alone rather than set. A zero width or height in a viewBox does not mean "empty", it means the element is not rendered at all — so measuring nothing, whether from an empty drawing or from one in a hidden part of the page, would blank a drawing that would otherwise have shown with the box
 * its author gave it.
 */
export function fitViewBox(drawing: SVGSVGElement): void {
	const { height, width, x, y } = drawing.getBBox();
	if (width <= 0 || height <= 0) {
		return;
	}
	// Outward in all four directions: the origin down, the size up. Rounding the origin up or the
	// size down would take a fraction of a unit off an edge the drawing had asked for.
	const left = Math.floor(x);
	const top = Math.floor(y);
	drawing.setAttribute('viewBox', `${left} ${top} ${Math.ceil(x + width - left)} ${Math.ceil(y + height - top)}`);
}

export function parseDrawing(markup: string): Result.Result<SVGSVGElement, DrawingParseError> {
	if (markup.trim() === '') {
		return failure('the drawing was empty', markup);
	}
	// Named apart from the page's own document rather than shadowing it: everything below reads
	// off the one the parser just made, and a reader should not have to work that out.
	const drawingDocument = parser.parseFromString(markup, SVG_MEDIA_TYPE);
	const parserError = drawingDocument.querySelector(PARSER_ERROR_SELECTOR);
	if (parserError !== null) {
		// Interpolated rather than defaulted, because `textContent` is only nullable on a Node in
		// general: an element always has one, so a fallback would be a branch nothing can reach.
		return failure(`the drawing could not be parsed: ${parserError.textContent}`, markup);
	}
	const { documentElement } = drawingDocument;
	if (!(documentElement instanceof SVGSVGElement)) {
		return failure(`the drawing is a <${documentElement.nodeName}>, not an <svg>`, markup);
	}
	return Result.succeed(documentElement);
}
